import axios from 'axios';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { API, DISTRIBUTOR, DOMAIN, toCamelCase } from '@autopass-web/domain';
import {
  Interceptor,
  parseResponse,
  UserHttpClient,
} from '@autopass-web/mochi-web-api';

import { DIST } from '../config';
import storage from './storage';

const currentDistributor = process.env.NEXT_PUBLIC_DISTRIBUTOR; // REMOVE

class HeaderConfigInterceptor extends Interceptor {
  onIntercept = (request) => {
    const accessToken = () => {
      const accessToken = get(storage.getAuth(), 'accessToken', undefined);
      return accessToken ? `Bearer ${accessToken}` : '';
    };

    const token = accessToken();
    if (token) request.headers['Authorization'] = token;

    request.headers['Mochipay-Client-Id'] =
      DOMAIN.DISTRIBUTOR_CLIENT_UID[currentDistributor];
    return request;
  };
}

class RefreshTokenInterceptor extends Interceptor {
  constructor() {
    super();
    this.isRefreshing = false;
    this.failedQueue = [];
  }

  processQueue = (error, token) => {
    this.failedQueue.forEach((promise) => {
      if (error) {
        return promise.reject(error);
      } else {
        return promise.resolve(token);
      }
    });

    this.failedQueue = [];
  };

  onIntercept = (error, http) => {
    if (error.response.status === 401) {
      if (DIST === DISTRIBUTOR.RICHART_LIFE) {
        storage.clearAuth();
        window.location.href = `http://${window.location.host}/expired`;
        return;
      }

      // 已將 isRefreshing flag
      if (this.isRefreshing) {
        return new Promise((resolve, reject) => {
          this.failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            error.response.config.headers['Authorization'] = 'Bearer ' + token;
            return http(error.response.config);
          })
          .catch((err) => {
            return err;
          });
      }

      this.isRefreshing = true;

      return new Promise((resolve, reject) => {
        const oauthApiUrl = `${
          DOMAIN.API_URL[API.USER].baseUrl
        }/oauth/token`.replace('/v3', '');
        axios
          .post(oauthApiUrl, {
            refresh_token: get(storage.getAuth(), 'refreshToken', ''),
            grant_type: 'refresh_token',
          })
          .then((res) => {
            storage.saveAuth({
              ...toCamelCase(res.data),
              scopes: res.data.scope,
            });
            this.processQueue(null, res.data.access_token);
            error.response.config.headers['Authorization'] =
              'Bearer ' + res.data.access_token;
            resolve(http(error.response.config));
          })
          .catch((err) => {
            this.processQueue(err, null);
            storage.clearAuth();
            storage.clearSkipLogin();

            if (typeof window !== 'undefined') {
              window.location.href = `/user/welcome`;
            }
            reject(err);
          })
          .finally(() => {
            this.isRefreshing = false;
          });
      }).catch((error) => {
        if (typeof window !== 'undefined') {
          window.location.href = '/user/welcome';
        }
      });
    } else {
      throw error;
    }
  };
}

const http = new UserHttpClient({
  requestInterceptorChain: [new HeaderConfigInterceptor()],
  responseResultInterceptorChain: [],
  responseErrorInterceptorChain: [new RefreshTokenInterceptor()],
});

class Repository {
  fetchAutopayBoard = async () => {
    const codes = 'auto_pay_street_parking_board';
    const { error, data } = await http.boards({ codes });
    return { error, data: data?.data.data.autoPayStreetParkingBoard };
  };

  fetchSearchBillBoard = async () => {
    const codes = 'search_street_parking_board';
    const { error, data } = await http.boards({ codes });
    return { error, data: data?.data.data.searchStreetParkingBoard };
  };

  fetchMainPagePromotionBoard = async () => {
    const codes = 'main_page_board';
    const { error, data } = await http.boards({ codes });
    return { error, data: data?.data.data.mainPageBoard };
  };

  fetchMainPageNotificationBoard = async () => {
    const codes = 'main_page_notification_board';
    const { error, data } = await http.boards({ codes });
    return { error, data: data?.data.data.mainPageNotificationBoard };
  };

  fetchCoinPageRulesBoard = async () => {
    const codes = 'auto_coin_rules_board';
    const { error, data } = await http.boards({ codes });
    return { error, data: data?.data.data.autoCoinRulesBoard };
  };

  fetchBootstraps = async () => {
    const { error, data } = await http.bootstraps();
    return { error, data: data?.data.data };
  };

  login = async ({ mobile, code }) => {
    const { error, data } = await http.mobileLogin({ mobile, code });
    if (error) return { error, data: null };
    storage.saveAuth(data.data.data);
    return { error, data: data?.data.data };
  };

  register = async ({ email, plateNumber, carType }) => {
    const { error, data } = await http.register({
      email,
      plateNumber,
      carType,
    });
    if (error) return { error, data: null };
    storage.saveAuth(data.data.data);
    return { error, data: data?.data.data };
  };

  logout = async () => {
    const { error } = await http.logout();
    return { error, data: '' };
  };

  fetchOtp = async (mobile) => {
    const { error, data } = await http.fetchOtp(mobile);
    return { error, data: data?.data.data };
  };

  fetchUserInfo = async () => {
    const { error, data } = await http.fetchUser();
    return { error, data: data?.data.data };
  };

  updateUserInfo = async ({
    email = undefined,
    displayName = undefined,
    receipt = undefined,
    enableOnStreetParkingAutoPay = undefined,
    agreeTermsOfTaipeiCityParkinglotService = undefined,
  }) => {
    const { error, data } = await http.updateUserInfo({
      email,
      displayName,
      receipt,
      enableOnStreetParkingAutoPay,
      agreeTermsOfTaipeiCityParkinglotService,
    });
    return { error, data: data?.data.data };
  };

  agreeTermsOfTaipeiParkinglotService = async () => {
    const { error, data } = await http.updateUserInfo({
      agreeTermsOfTaipeiCityParkinglotService: true,
    });
    return { error, data: data?.data.data };
  };

  toggleStreetParkingAutopay = async (enableOnStreetParkingAutoPay) => {
    const { error, data } = await http.updateUserInfo({
      email: undefined,
      displayName: undefined,
      receipt: undefined,
      enableOnStreetParkingAutoPay,
    });
    return { error, data: data?.data.data };
  };

  updateUserReceipt = async (receipt) => {
    const { error, data } = await http.updateUserInfo({ receipt });
    return { error, data: data?.data.data };
  };

  fetchStreetParkingBills = async ({
    city = undefined,
    plateNumber = undefined,
  }) => {
    const { error, data } = await http.fetchStreetParkingBills({
      city,
      plateNumber,
    });
    return { error, data: data?.data.data };
  };

  payStreetParkingBills = async ({
    city = undefined,
    codes = undefined,
    plateNumber = undefined,
  }) => {
    const { error, data } = await http.payStreetParkingBills({
      city,
      codes,
      plateNumber,
    });
    return { error, data: data?.data.data };
  };

  fetchLicensePlates = async () => {
    const { error, data } = await http.fetchLicensePlates();
    return { error, data: data?.data.data };
  };

  addLicensePlate = async ({ plateNumber, vehicleType, energySource }) => {
    const { error, data } = await http.addLicensePlate({
      plateNumber,
      vehicleType,
      energySource,
    });
    return { error, data: data?.data.data };
  };

  deleteLicensePlate = async (id) => {
    const { error, data } = await http.deleteLicensePlate(id);
    return { error, data: data?.data.data };
  };

  bindLicensePlate = async (id) => {
    const { error, data } = await http.bindLicensePlate(id);
    return { error, data: data?.data.data };
  };

  modifyLicensePlate = async ({ id, plateNumber, vehicleType }) => {
    const { error, data } = await http.modifyLicensePlate({
      id,
      plateNumber,
      vehicleType,
    });
    return { error, data: data?.data.data };
  };

  initMochipayValidation = async () => {
    const { error, data } = await http.initMochipayValidation();
    return { error, data: data?.data.data };
  };

  bindCpcPay = async (cpcId) => {
    if (cpcId === '') {
      return { error: '請先設定你的中油 Pay 付款設定' };
    }

    const { error, data } = await http.bindCpcPay(cpcId);
    return { error, data: data?.data.data };
  };

  fetchNpcCards = async () => {
    const { error, data } = await http.fetchNpcCards();
    return { error, data: data?.data.data };
  };

  bindNpcPay = async ({ cardType, enableRedeem = true }) => {
    const { error, data } = await http.bindNpcPay({ cardType, enableRedeem });
    return { error, data: data?.data.data };
  };

  enableRedeemDiscount = async () => {
    const { error, data } = await http.enableRedeemDiscount();
    return { error, data: data?.data.data };
  };

  disableRedeemDiscount = async () => {
    const { error, data } = await http.disableRedeemDiscount();
    return { error, data: data?.data.data };
  };

  bindMochipayWallet = async ({ token, transactionId }) => {
    const { error, data } = await http.bindMochipayWallet({
      token,
      transactionId,
    });
    return { error, data: data?.data.data };
  };

  deleteCreditCard = async () => {
    const { error } = await http.deleteCreditCard();
    return { error, data: '' };
  };

  fetchDebitAccountTransactions = async ({
    id,
    startAt,
    endAt,
    page,
    totalPage,
  }) => {
    const { error, data } = await http.fetchDebitAccountTransactions({
      id,
      startAt,
      endAt,
      page,
      totalPage,
    });
    return { error, data: data?.data };
  };

  fetchOrders = async ({ page = 1, pageSize = 100, transactionTypes = [] }) => {
    const { error, data } = await http.fetchOrders({
      page,
      pageSize,
      transactionTypes:
        transactionTypes.length === 0 ? undefined : transactionTypes.join(','),
    });
    return { error, data: data?.data };
  };

  fetchOrderDetail = async (url) => {
    const { error, data } = await http.fetchOrderDetail(url);
    return { error, data: data?.data };
  };

  fetchMochiCoins = async ({ state, page }) => {
    const { error, data } = await http.fetchMochiCoins({ state, page });
    return { error, data: data?.data };
  };

  fetchParkingRecord = async () => {
    const { error, data } = await http.fetchParkingRecord();
    return { error, data: data?.data.data };
  };

  parkingRecordCancel = async (parkingRecordId) => {
    const { error, data } = await http.parkingRecordCancel(parkingRecordId);
    return { error, data: data?.data.data };
  };

  parkingRecordLeave = async (parkingRecordId) => {
    const { error, data } = await http.parkingRecordLeave(parkingRecordId);
    return { error, data: data?.data.data };
  };

  updatePushToken = async (token) => {
    //如果 token 是 null | undefined | empty string | 'null'，就不要打 api 上傳
    if (isEmpty(token) || token === 'null') {
      return { error: '', data: '' };
    }

    const { error } = await http.updatePushToken(token);
    return { error };
  };

  fetchRefuelingRequestsLatest = async () => {
    const { error, data } = await http.fetchRefuelingRequestsLatest();
    return { error, data: data?.data.data };
  };

  answerRefuelingRequests = async ({ id, answer }) => {
    const { error, data } = await http.answerRefuelingRequests({ id, answer });
    return { error, data: data?.data.data };
  };

  fetchOilPricing = async () => {
    const { error, data } = await parseResponse(
      axios.get('https://mochi-cloud-api.autopass.xyz/v1/gas_pricing'),
    );
    return { error, data: data?.data.data };
  };

  fetchOneTimeToken = async (token) => {
    const { error, data } = await http.fetchOneTimeToken(token);
    if (data) {
      storage.saveAuth(data.data.data);
    }
    return { error, data: data?.data.data };
  };
}

export default new Repository();
